import { useEffect, useSyncExternalStore } from 'react';

/**
 * The bottom tab bar steps out of the way while a long list is being scrolled DOWN and
 * comes back the moment the scroll turns up (Oct 2026, the MESSAGES inbox first).
 *
 * One tiny store, no context: the bar is mounted at the app root and the scroller that
 * drives it is deep inside a page, so a module-level value both can read is the simplest
 * thing that works. Whoever hides it is responsible for showing it again — the hook below
 * does that on unmount — and a route change always shows it (MobileTabBar resets).
 */

let hidden = false;
const listeners = new Set();

function emit() { listeners.forEach((l) => l()); }

export function setTabBarHidden(next) {
  const v = !!next;
  if (v === hidden) return;
  hidden = v;
  emit();
}

export const hideTabBar = () => setTabBarHidden(true);
export const showTabBar = () => setTabBarHidden(false);
export const isTabBarHidden = () => hidden;

export function useTabBarHidden() {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    () => hidden,
    () => false,
  );
}

/** How far a scroll has to travel in one direction before the bar moves. */
export const SCROLL_HIDE_DELTA = 12;
/** Near the top the bar is always shown, whichever way the last scroll went. */
export const SCROLL_TOP_ZONE = 24;

/**
 * Pure: given the last scrollTop and the new one, which way should the bar go?
 * Returns true (hide), false (show) or null (no change — too small a move).
 */
export function tabBarHiddenAfterScroll(prevTop, nextTop, { maxTop = Infinity } = {}) {
  if (nextTop <= SCROLL_TOP_ZONE) return false;
  // Rubber-banding past the end reports a scrollTop beyond the max: ignore it.
  if (nextTop > maxTop) return null;
  const delta = nextTop - prevTop;
  if (delta > SCROLL_HIDE_DELTA) return true;
  if (delta < -SCROLL_HIDE_DELTA) return false;
  return null;
}

/**
 * Attach to a scroller: hide the bar on a downward scroll, show it on an upward one, and
 * always show it again when the scroller goes away or the hook is disabled.
 */
export function useHideTabBarOnScroll(ref, { enabled = true } = {}) {
  useEffect(() => {
    const el = ref?.current;
    if (!enabled || !el) return undefined;
    let last = el.scrollTop;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const top = el.scrollTop;
        const maxTop = el.scrollHeight - el.clientHeight;
        const next = tabBarHiddenAfterScroll(last, top, { maxTop });
        if (Math.abs(top - last) > SCROLL_HIDE_DELTA || top <= SCROLL_TOP_ZONE) last = top;
        if (next !== null) setTabBarHidden(next);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
      setTabBarHidden(false);
    };
  }, [ref, enabled]);
}
